/* Home page updated to remove the parallax divider and advantages strip.
   Hero image preload is now scoped to this page only (via Helmet) so that
   product, blog and other pages no longer trigger the browser warning
   "preloaded but not used within a few seconds". */

import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import Header from '@/components/Header';
import Hero from '@/components/Hero';
import CategoryCards from '@/components/CategoryCards';
import Assortiment from '@/components/Assortiment';
import Newsletter from '@/components/Newsletter';
import Blogs from '@/components/Blogs';
import Reviews from '@/components/Reviews';
import FAQ from '@/components/FAQ';
import Footer from '@/components/Footer';

export default function HomePage() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const targetId = hash.substring(1);
        const element = document.getElementById(targetId);
        if (element) {
          const headerHeight = 120;
          const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerHeight;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Buitn Planten | Alles voor buiten, groen samengebracht</title>
        <meta name="description" content="Ontdek haagplanten, bomen en tuinplanten voor een buitenruimte die klopt. Buitn Planten, onderdeel van Buitn.nl." />
        <meta name="keywords" content="haagplanten, bomen, tuinplanten, buitenplanten, Buitn Planten, Buitn.nl" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Buitn Planten | Alles voor buiten, groen samengebracht" />
        <meta property="og:description" content="Haagplanten, bomen en tuinplanten voor een buitenruimte die klopt." />
        <meta property="og:url" content="https://buitnplanten.nl/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://buitnplanten.nl/assets/sections/hero.webp" />
        <link rel="canonical" href="https://buitnplanten.nl/" />
        <link rel="preload" as="image" href="/assets/sections/hero.webp" fetchpriority="high" />

        {/* WebSite Schema with SearchAction */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "Buitn Planten",
            "url": "https://buitnplanten.nl/",
            "description": "Haagplanten, bomen en tuinplanten voor een buitenruimte die klopt.",
            "inLanguage": "nl-NL",
            "publisher": {
              "@type": "Organization",
              "name": "Buitn",
              "url": "https://www.buitn.nl/",
              "logo": "https://buitnplanten.nl/assets/icon.webp",
              "sameAs": [
                "https://www.instagram.com/buitnplanten.nl/",
                "https://share.google/rRuGOX9cwYp6vbKci"
              ]
            }
          })}
        </script>
      </Helmet>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="min-h-screen bg-jv-primary"
      >
        <Header />
        <main className="jv-homepage">
          <Hero />
          <CategoryCards />
          <Assortiment />
          <Newsletter />
          <Blogs />
          <Reviews />
          <FAQ />
          <Footer />
        </main>
      </motion.div>
    </>
  );
}
